import * as Logger from "js-logger";
import { AssetConfigSchema } from "slotworks/config/asset-config-schema";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import AnimationManager from "slotworks/graphics/animation/animation-manager";
import Layers from "slotworks/graphics/layers/layers";
import SpineManager from "slotworks/graphics/spine/spine-manager";
import DeviceInfo from "slotworks/utils/device-info";
import Rectangle from "slotworks/utils/geom/rectangle";

declare const __DEBUG__: boolean;

class GraphicsManager {

    private textures: Map<string, PIXI.Texture> = new Map<string, PIXI.Texture>();

    /**
     * Call this after preloading.
     *
     * Creates a pixi renderer (WebGL with canvas fallback), an HTML canvas element and a pixi stage container
     * Uses graphics config to intialize factory methods
     * Starts render loop (requestAnimationFrame)
     *
     * @param config {GraphicsConfigSchema}
     */
    public init(config: AssetConfigSchema) {
        const layoutConfig = config.layouts[DeviceInfo.layoutId].graphics;

        Layers.init(layoutConfig);

        const animationConfig = config.layouts.all.animations;

/////////////////////////
////////////////////////
//////////////////////////////////////
/////////
//////////////////

        AnimationManager.init(animationConfig);
    }

    /**
     * Registers a texture with the graphics manager
     *
     * @param id {string}
     * @param texture {PIXI.Texture}
     */
    public addTexture(id: string, texture: PIXI.Texture) {
        this.textures.set(id, texture);
    }

    /**
     * Check for the existence of a texture
     * @param id {string}
     */
    public hasTexture(id: string): boolean {
        return this.textures.has(id);
    }

    /**
     * Returns a reference to a texture previously registered
     * Don't use this method for sprite creation, instead use createSprite factory method
     *
     * @param id {string}
     * @returns {PIXI.Texture}
     */
    public getTexture(id: string): PIXI.Texture {
        if (!this.hasTexture(id)) {
            throw new Error("Texture `" + id + "` does not exist in texture cache");
        }
        return this.textures.get(id);
    }

    /**
     * Destroys a texture and removes it from the cache
     *
     * @param id {string}
     * @param texture {PIXI.Texture}
     */
    public destroyTexture(id: string) {
        const texture = this.getTexture(id);
        texture.destroy(true);

        this.textures.delete(id);
    }

    /**
     * Factory method to instantiate a new Sprite from a given ID
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param id {string}
     * @returns {PIXI.Sprite}
     */
    public createSprite(id: string): PIXI.Sprite;

    /**
     * Factory method to instantiate a new Sprite from a list of IDs, will keep trying until it finds one which exists, or return null
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param ids {string}
     * @returns {PIXI.Sprite}
     */
    public createSprite(ids: string[]): PIXI.Sprite;

    public createSprite(id: string | string[]): PIXI.Sprite {
        let ids;
        if (typeof id === "string") {
            ids = [id];
        } else {
            ids = id;
        }

        let sprite = null;

        while (ids.length > 0) {
            const spriteId = ids.shift();

            if (!this.textures.has(spriteId)) {
                if (typeof id === "string") {
                    throw new Error("Invalid texture id: " + id);
                }
            } else {
                sprite = new PIXI.Sprite(this.textures.get(spriteId));
            }
        }

        return sprite;
    }

    /**
     * Creates an animation by name
     */
    public createAnimation(id: string, position?: Rectangle): AnimatedSprite;

    /**
     * Goes through the list and returns the first valid animation
     */
    public createAnimation(ids: string[], position?: Rectangle): AnimatedSprite;

    /**
     * Factory method to instantiate a new AnimatedSprite.
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     */
    public createAnimation(ids: string | string[], position?: Rectangle): AnimatedSprite {

        let animation = null;

        if (typeof ids === "string") {
            animation = AnimationManager.generateAnimation(ids);
        } else {
            animation = AnimationManager.generateAnimationWithFallbacks(ids);
        }

        if (position) {
            animation.setPosition(position);
        }

        return animation;
    }

    public createSpine(name: string) {
        return SpineManager.createSpine(name);
    }

    /**
     * Factory method to instantiate a new AnimatedSprite combining frames over multiple animations
     * Config (fps, loop etc) is taken from first in the list
     */
    public createMergedAnimation(ids: string[], position?: Rectangle): AnimatedSprite {
        let animation = null;

        animation = AnimationManager.generateAnimation(ids);

        if (position) {
            animation.setPosition(position);
        }

        return animation;
    }

    private listAllFonts(config: AssetConfigSchema) {
        const fontDictionary = new Map<string, boolean>();

        for (const layoutName in config.layouts) {
            if (config.layouts.hasOwnProperty(layoutName)) {
                const layout = config.layouts[layoutName];

                if (layout.graphics) {
                    for (const graphicName in layout.graphics) {
                        if (layout.graphics.hasOwnProperty(graphicName)) {
                            const graphic = layout.graphics[graphicName];
                            if (graphic.text) {
                                for (const text of graphic.text) {
                                    fontDictionary.set(text.fontFamily + "-" + text.fontVariant, true);
                                }
                            }
                        }
                    }
                }
            }
        }

        Logger.info("Used fonts:");
        fontDictionary.forEach((value, index) => Logger.info(index));
    }
}

export default new GraphicsManager();
